// Donut with labels around the ring, and a compact key for the slices too small to label.
//
// Outside labels only work when slices are reasonably even. Competitive share data is not:
// one advertiser routinely holds 70%+ and the rest are slivers occupying a few degrees of
// arc, so every one of their labels lands on the same side and stacks into a column with
// leader lines raking across the chart.
//
// So a slice is labelled on the ring only if it is big enough to point at cleanly; the rest
// are listed underneath with their colour, value and share. Nothing is hidden — the small
// advertisers are still named and still measured, they just are not worth a leader line.

import { fmtNumber } from '../format.js';
import { T } from '../../../shared/styles/tokens.js';

const W = 460, H = 250, CX = 230, CY = 124, R = 64, STROKE = 22;
const GAP = 32;                 // minimum vertical distance between two labels
const MIN_FRAC = 0.04;          // below this a slice goes to the key instead
const MAX_LABELS = 6;
// The leader line turns horizontal only once clear of the ring. Bending at the segment's
// own radius sends the horizontal run back across the donut whenever a label has been
// pushed away from its segment.
const CLEAR = R + STROKE / 2 + 14;

function spread(items) {
  items.sort((a, b) => a.y - b.y);
  for (let i = 1; i < items.length; i++) {
    if (items[i].y - items[i - 1].y < GAP) items[i].y = items[i - 1].y + GAP;
  }
  if (items.length) {
    const over = items[items.length - 1].y - (H - 20);
    if (over > 0) items.forEach(it => (it.y -= over));
    const under = 20 - items[0].y;
    if (under > 0) items.forEach(it => (it.y += under));
  }
  return items;
}

export default function DonutChart({ segments, centerLabel, format = 'number' }) {
  const items = (segments || []).filter(s => Number(s.value) > 0);
  const total = items.reduce((sum, s) => sum + Number(s.value), 0);
  if (total === 0) return <p className="muted">No data.</p>;

  const money = format === 'currency';
  const money$ = (v) => (money ? `$${fmtNumber(v)}` : fmtNumber(v));

  const C = 2 * Math.PI * R;
  let offset = 0;
  const arcs = items.map(s => {
    const frac = Number(s.value) / total;
    const mid = (offset + frac / 2) * 2 * Math.PI - Math.PI / 2;
    const arc = { ...s, frac, dash: frac * C, offset: offset * C, mid };
    offset += frac;
    return arc;
  });

  const labelled = arcs.filter(a => a.frac >= MIN_FRAC).slice(0, MAX_LABELS);
  const rest = arcs.filter(a => !labelled.includes(a));

  const right = spread(labelled
    .filter(a => Math.cos(a.mid) >= 0)
    .map(a => ({ ...a, side: 1, y: CY + Math.sin(a.mid) * CLEAR })));
  const left = spread(labelled
    .filter(a => Math.cos(a.mid) < 0)
    .map(a => ({ ...a, side: -1, y: CY + Math.sin(a.mid) * CLEAR })));

  const label = (a) => {
    const anchor = a.side > 0 ? 'end' : 'start';
    const ringX = CX + Math.cos(a.mid) * (R + STROKE / 2 - 2);
    const ringY = CY + Math.sin(a.mid) * (R + STROKE / 2 - 2);
    const elbowX = CX + a.side * CLEAR;
    const textX = a.side > 0 ? W - 10 : 10;
    return (
      <g key={a.label}>
        <polyline
          points={`${ringX.toFixed(1)},${ringY.toFixed(1)} ${elbowX.toFixed(1)},${a.y.toFixed(1)} ${textX},${a.y.toFixed(1)}`}
          fill="none" stroke={a.color} strokeWidth="1" opacity="0.5" />
        <text x={textX} y={a.y - 3} textAnchor={anchor}
              style={{ fontFamily: T.fontUi, fontSize: 11, fontWeight: 600, fill: T.text }}>
          {a.label}
        </text>
        {/* Figures in mono — this is data, not a label. */}
        <text x={textX} y={a.y + 11} textAnchor={anchor}
              style={{ fontFamily: T.fontMono, fontSize: 11, fill: a.color }}>
          {money$(a.value)} ({(a.frac * 100).toFixed(1)}%)
        </text>
      </g>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="share chart"
           style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
        <g transform={`translate(${CX},${CY}) rotate(-90)`}>
          {arcs.map(a => (
            <circle key={a.label} r={R} cx="0" cy="0" fill="none"
                    stroke={a.color} strokeWidth={STROKE}
                    strokeDasharray={`${a.dash} ${C - a.dash}`}
                    strokeDashoffset={-a.offset}>
              <title>{`${a.label}: ${money$(a.value)} (${(a.frac * 100).toFixed(1)}%)`}</title>
            </circle>
          ))}
        </g>
        {centerLabel && (
          <text x={CX} y={CY + 4} textAnchor="middle" className="donut-center">{centerLabel}</text>
        )}
        {[...right, ...left].map(label)}
      </svg>

      {rest.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: `4px ${T.s4}px` }}>
          {rest.map(a => (
            <span key={a.label} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11,
            }}>
              <i style={{
                width: 8, height: 8, borderRadius: 2, background: a.color, flexShrink: 0,
              }} />
              <span style={{ color: T.text2 }}>{a.label}</span>
              <span className="o-mono" style={{ color: T.text3, fontSize: 11 }}>
                {money$(a.value)} ({(a.frac * 100).toFixed(1)}%)
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
